import React, { useCallback, useEffect } from 'react';
import { AddToCalendar } from '@shared/components/AddToCalendar';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useGetGuestSiteScheduleLazyQuery } from '@graphql/generated';
import { addressStr } from '@shared/utils/formatting/location.formats';
import { useGuestGuideTelemetry } from '../GuestGuideTelemetry';
import { useTranslation } from '@shared/core';
import { GuestGuideActionButton } from '../GuestGuideActionButton';
import { Calendar } from '@withjoy/joykit/icons';

/**
 * Converts a human-readable date string (e.g. "August 23, 2025") to UTC ISO string,
 * normalizing it to midnight UTC. This ensures consistent date handling across timezones
 * by stripping time components and using only the date portion.
 *
 * The UTC conversion is necessary to prevent unexpected date shifts when the calendar
 * library uses .getUTCDate(), since local timezone midnight could result in a different
 * UTC date depending on the user's timezone.
 *
 * @param finalizedDate - Human-readable date string (e.g. "August 23, 2025")
 * @returns UTC ISO string representing midnight of the given date
 */
const prepareFinalizedDate = (finalizedDate: string) => {
  const date = new Date(finalizedDate);
  const utcTimestamp = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());

  return new Date(utcTimestamp).toISOString();
};

export const AddToCalendarAction: React.FC = () => {
  const { eventHandle, eventInfo } = useEventInfo();
  const telemetry = useGuestGuideTelemetry();
  const { t } = useTranslation('guestGuide');
  const [querySchedule, { data, loading }] = useGetGuestSiteScheduleLazyQuery({
    batchMode: 'fast',
    ssr: false,
  });

  useEffect(() => {
    if (eventHandle) {
      querySchedule({
        variables: {
          eventHandle,
          isAdmin: false,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventHandle]);

  const finalizedDate = eventInfo?.finalizedDate?.dateString ? eventInfo.finalizedDate.dateString : undefined;
  const events = data?.eventByName?.info.schedule.formattedSchedules.flatMap(day => day.items);
  const mainEvent = events?.find(ev => ev.isMain);
  const eventData: Record<'startTime' | 'endTime' | 'description' | 'title' | 'location', string | undefined> = {
    startTime: undefined,
    endTime: undefined,
    description: undefined,
    title: undefined,
    location: undefined,
  };

  const handleClick = useCallback(() => {
    telemetry.clickAddToCalendar();
  }, [telemetry]);

  if (loading) {
    return null;
  } else if (mainEvent) {
    eventData.startTime = mainEvent.startTime?.timestamp ? new Date(mainEvent.startTime.timestamp).toISOString() : undefined;
    eventData.endTime = mainEvent.endTime?.timestamp ? new Date(mainEvent.endTime.timestamp).toISOString() : undefined;
    eventData.description = mainEvent.description || undefined;
    eventData.title = mainEvent.name || undefined;
    eventData.location = mainEvent.location || undefined;
  } else if (finalizedDate) {
    eventData.startTime = prepareFinalizedDate(finalizedDate);
    eventData.location = eventInfo?.locationInfo ? addressStr(eventInfo?.locationInfo) : undefined;
    eventData.title = eventInfo?.eventDisplayName || undefined;
  }

  if (!eventData.startTime) {
    return null;
  }

  return (
    <AddToCalendar
      start={eventData.startTime}
      end={eventData.endTime}
      allDay={!eventData.endTime}
      location={eventData.location}
      description={eventData.description}
      title={eventData.title}
    >
      <GuestGuideActionButton
        icon={<Calendar />}
        onClick={handleClick}
      >
        {t('buttonTextAddToCalendar')()}
      </GuestGuideActionButton>
    </AddToCalendar>
  );
};
